import React, { Component, Fragment } from 'react'
import i18n from 'i18next'
import * as R from 'ramda'
import { observer } from 'mobx-react'

import SearchLayout from '../Layouts/Search'
import DataTable from '../DataTable'
import Anchor from '../../UniformComponents/Anchor'
import Badge from '../../UniformComponents/Badge'
import Button from '../../UniformComponents/Button'
import Tooltip from '../../UniformComponents/Tooltip'
import Select from '../form/Select'
import Checkbox from '../form/Checkbox'
import VenueEdit from './VenueEdit'
import VenueStore from './VenueStore'
import { removeQueryParam, setQueryParam, getQueryParam } from '../../utils/helpers'

const VENUE_PARAM = 'venueId'

@observer
class VenueSearch extends Component {
    constructor(props) {
        super(props)
        this.state = {
            showModal: false,
            venueId: 0,
        }
        this.venueStore = new VenueStore(props.store)
        this.setColumns()
    }

    async componentDidMount() {
        const { store, location } = this.props
        store.breadcrumps = [
            {
                name: 'venue',
                url: '/venue',
                title: 'Venue',
            },
        ]
        await this.venueStore.initSearch()

        this.checkQueryParam(location)
    }

    checkQueryParam = (location) => {
        const venueId = getQueryParam(location, VENUE_PARAM)
        if (venueId === -1) return
        if (venueId !== 0) {
            this.setState({ venueId })
        }
        this.toggleModal()
    }

    setColumns = () => {
        const { venueStore } = this
        this.columns = [
            {
                name: i18n.t('status', 'Status'),
                key: 'active',
                cellClass: 'status',
                dataTransform: (active) => {
                    switch (active) {
                        case true: return (
                            <Badge
                                type="success"
                                cssClass="status-badge"
                                label={i18n.t('userSearch.table.active', 'Active')}
                                icon="fa-regular fa-circle-check"
                            />
                        )
                        case false: return (
                            <Badge
                                type="error"
                                cssClass="status-badge"
                                label={i18n.t('userSearch.table.inactive', 'Inactive')}
                                icon="fa-regular fa-ban"
                            />
                        )
                        default: return null
                    }
                },
            },
            {
                name: i18n.t('venue', 'Venue'),
                key: 'name',
                cellClass: 'bold',
            },
            {
                name: i18n.t('city', 'Town / city'),
                key: 'city',
                cellClass: 'bold',
            },
            {
                name: i18n.t('country', 'Country'),
                key: 'country',
                cellClass: 'bold',
                dataTransform: (country) => (
                    R.pathOr('', ['search', 'countryMap', country], venueStore)
                ),
            },
            {
                name: '',
                key: 'venueId',
                cellClass: 'center',
                dataTransform: (venueId) => (
                    <div className="survey-btn-wrapper">
                        <Anchor
                            cssClass="icon-link-btn"
                            onClick={() => { this.handleOpenModal(venueId) }}
                        >
                            <Tooltip
                                text={i18n.t('venue.search.editVenue', 'Edit Venue')}
                                direction="bottom"
                            />
                            <i className="fa-light fa-edit" />
                        </Anchor>
                    </div>
                ),
            },
        ]
    }

    toggleModal = () => {
        this.setState((prevState) => ({
            showModal: !prevState.showModal,
        }))
    }

    handleOpenModal = (venueId) => {
        const { location, history } = this.props

        setQueryParam(location, history, VENUE_PARAM, venueId)
        this.setState({ venueId })
        this.toggleModal()
    }

    handleCloseModal = () => {
        const { location, history } = this.props

        removeQueryParam(location, history, VENUE_PARAM)
        this.setState({ venueId: 0 })
        this.toggleModal()
        this.venueStore.resetFormState()
    }

    renderFilterOptions = () => {
        const { venueStore } = this
        return (
            <Fragment>
                <div className="filter-texts">
                    <i className="filter-icon fa-regular fa-bars-filter" />
                    <p className="filter-text">{i18n.t('searchLayout.filters')}</p>
                </div>
                <fieldset className="filter">
                    <Select
                        label={i18n.t('country', 'Country')}
                        options={venueStore.search.countries}
                        onBlur={venueStore.changeCountry}
                        value={venueStore.filters.country}
                    />
                </fieldset>
                <fieldset className="filter">
                    <Select
                        label={i18n.t('city', 'Town / city')}
                        options={venueStore.search.cities}
                        onBlur={venueStore.changeCity}
                        value={venueStore.filters.city}
                    />
                </fieldset>
                <fieldset className="filter">
                    <legend className="legend-title">
                        {i18n.t('venue.search.eventStatus', 'Event status')}
                    </legend>
                    {Object.keys(venueStore.filters.status).map((key) => (
                        <Checkbox
                            name={venueStore.filters.status[key].name}
                            onBlur={() => {
                                venueStore.toggleStatus(venueStore.filters.status[key].name)
                            }}
                            checked={venueStore.filters.status[key].value}
                            label={venueStore.filters.status[key].label}
                        />
                    ))}
                </fieldset>
            </Fragment>
        )
    }

    renderButtons = () => {
        const { store } = this.props
        return (
            <div className="right">
                {store.userData.role < 150 && (
                    <Button
                        cssClass="add-button primary regular"
                        onClick={() => { this.handleOpenModal(0) }}
                        icon="fa-light fa-user-plus"
                        label={i18n.t('add_venue', 'Add new venue')}
                    />
                )}
            </div>
        )
    }

    renderTablePanel = () => {
        const { venueStore, columns } = this
        return (
            <DataTable
                store={venueStore}
                inMemory
                dataSet={venueStore.filteredList || []}
                columns={columns}
            />
        )
    }

    renderGuide = () => (
        <div dangerouslySetInnerHTML={{
            __html: i18n.t('venue.introText'),
        }}
        />
    )

    render() {
        const { store } = this.props
        const { showModal, venueId } = this.state
        return (
            <Fragment>
                <SearchLayout
                    pageTitle={i18n.t('venues')}
                    renderFilterOptions={this.renderFilterOptions}
                    renderButtons={this.renderButtons}
                    renderTablePanel={this.renderTablePanel}
                    renderGuide={this.renderGuide}
                    searchStore={this.venueStore}
                />
                <VenueEdit
                    store={store}
                    venueStore={this.venueStore}
                    venueId={venueId}
                    showModal={showModal}
                    handleCloseModal={this.handleCloseModal}
                />
            </Fragment>
        )
    }
}

export default VenueSearch
